import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../images/Logo_head.png";
import { AuthContext } from "../context/AuthContext";

const colors = {
  mainBlue: "#061941",
  xanthous: "#F2AF29",
  isabelline: "#F4F3EE",
  carrieban: "#3C6E71",
  cafeNoir: "#583E23",
};

function SideBar() {
  const navigate = useNavigate();
  const { isLoggedIn, userRole, setIsLoggedIn, setUserRole } =
    useContext(AuthContext);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleDashboardClick = () => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData && userData.role === "professor") {
      navigate("/professor-dashboard");
    } else if (userData && userData.role === "student") {
      navigate("/student-dashboard");
    } else {
      navigate("/");
    }
  };

  const handleWelcomeClick = () => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData && userData.role === "professor") {
      navigate("/professor-welcome");
    } else if (userData && userData.role === "student") {
      navigate("/student-welcome");
    } else {
      navigate("/");
    }
  };

  const handleMatchingClick = () => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData && userData.role === "professor") {
      navigate("/professor-matching");
    } else if (userData && userData.role === "student") {
      navigate("/student-matching");
    } else {
      navigate("/");
    }
  };

  const handleSavedClick = () => {
    navigate("/saved");
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    setUserRole(null);
    navigate("/");
  };

  const iconStyle = {
    width: "20px",
    height: "20px",
    minWidth: "20px",
    marginRight: isExpanded ? "12px" : "0",
    transition: "margin-right 0.3s ease",
  };

  const navItemStyle = {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    color: colors.mainBlue,
    textDecoration: "none",
    fontWeight: 600,
    fontSize: "14px",
    borderRadius: "8px",
    marginBottom: "4px",
    transition: "all 0.3s ease",
    cursor: "pointer",
    border: "none",
    background: "none",
    width: "100%",
    textAlign: "left",
    justifyContent: isExpanded ? "flex-start" : "center",
  };

  const navItemHoverStyle = {
    backgroundColor: colors.isabelline,
    color: colors.xanthous,
  };

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        height: "100vh",
        width: isExpanded ? "240px" : "70px",
        background: "white",
        boxShadow: `2px 0 8px 0 ${colors.mainBlue}15`,
        borderRight: `1px solid ${colors.mainBlue}20`,
        transition: "width 0.3s ease",
        zIndex: 2000,
        overflow: "hidden",
      }}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <div style={{ padding: "20px 16px" }}>
        {/* Logo */}
        <Link
          to="/"
          style={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            marginBottom: "30px",
            whiteSpace: "nowrap",
            justifyContent: isExpanded ? "flex-start" : "center",
          }}
        >
          <img
            src={logo}
            alt="NeuHawk Logo"
            style={{ height: "32px", width: "auto", minWidth: "32px" }}
          />
          {isExpanded && (
            <span
              style={{
                fontWeight: 800,
                fontFamily: "Poppins, Segoe UI, Arial, sans-serif",
                marginLeft: "12px",
                fontSize: "18px",
                color: colors.mainBlue,
                transition: "opacity 0.3s ease",
              }}
            >
              NeuHawk
            </span>
          )}
        </Link>

        {/* Navigation */}
        <nav style={{ fontFamily: "Nunito, Segoe UI, Arial, sans-serif" }}>
          {/* Welcome */}
          {isLoggedIn && (
            <button
              onClick={handleWelcomeClick}
              style={navItemStyle}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, navItemStyle)
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
              </svg>
              {isExpanded && (
                <span
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  Welcome
                </span>
              )}
            </button>
          )}

          {/* Admin Dashboard - Only for admins */}
          {isLoggedIn && userRole === "admin" && (
            <Link
              to="/admin"
              style={navItemStyle}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, navItemStyle)
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              {isExpanded && (
                <span
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  Admin Dashboard
                </span>
              )}
            </Link>
          )}

          {/* Analytics Dashboard - Only for admins */}
          {isLoggedIn && userRole === "admin" && (
            <Link
              to="/analytics-dashboard"
              style={navItemStyle}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, navItemStyle)
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
              </svg>
              {isExpanded && (
                <span
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  Analytics Dashboard
                </span>
              )}
            </Link>
          )}

          {/* Positions Admin - Only for admins */}
          {isLoggedIn && userRole === "admin" && (
            <Link
              to="/admin/positions"
              style={navItemStyle}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, navItemStyle)
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z" />
              </svg>
              {isExpanded && (
                <span
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  Positions Admin
                </span>
              )}
            </Link>
          )}

          {/* Pools */}
          {isLoggedIn && userRole === "professor" && (
            <button
              onClick={handleDashboardClick}
              style={navItemStyle}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, navItemStyle)
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
              </svg>
              {isExpanded && (
                <span
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  Talent Pool
                </span>
              )}
            </button>
          )}

          {isLoggedIn && userRole === "professor" && (
            <Link
              to="/professor-positions"
              style={navItemStyle}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, navItemStyle)
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z" />
              </svg>
              {isExpanded && (
                <span
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  My Positions
                </span>
              )}
            </Link>
          )}

          {isLoggedIn && userRole === "student" && (
            <Link
              to="/applications"
              style={navItemStyle}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, navItemStyle)
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,3H14.82C14.4,1.84 13.3,1 12,1C10.7,1 9.6,1.84 9.18,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M12,3A1,1 0 0,1 13,4A1,1 0 0,1 12,5A1,1 0 0,1 11,4A1,1 0 0,1 12,3" />
                <rect x="6" y="8" width="12" height="2" fill="white" />
                <rect x="6" y="12" width="10" height="2" fill="white" />
                <rect x="6" y="16" width="8" height="2" fill="white" />
              </svg>
              {isExpanded && (
                <span
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  My Applications
                </span>
              )}
            </Link>
          )}

          {isLoggedIn && userRole === "student" && (
            <Link
              to="/student-dashboard"
              style={navItemStyle}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, navItemStyle)
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z" />
              </svg>
              {isExpanded && (
                <span
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  Position Pool
                </span>
              )}
            </Link>
          )}
        
          {/* {isLoggedIn && userRole === "student" && (
            <Link
              to="/professor-pool"
              style={navItemStyle}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, navItemStyle)
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
              </svg>
              {isExpanded && (
                <span
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  Professor Pool
                </span>
              )}
            </Link>
          )}  */}
          {isLoggedIn && userRole === "student" && (
            <button
              onClick={handleMatchingClick}
              style={navItemStyle}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, navItemStyle)
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
              {isExpanded && (
                <span
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  Matching
                </span>
              )}
            </button>
          )}
          {isLoggedIn && (
            <button
              onClick={handleSavedClick}
              style={navItemStyle}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, navItemStyle)
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
              {isExpanded && (
                <span style={{ whiteSpace: "nowrap" }}>
                  {userRole === "student"
                    ? "Saved Positions"
                    : "Saved Students"}
                </span>
              )}
            </button>
          )}
          {isLoggedIn && (
            <Link
              to="/profile"
              style={navItemStyle}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, navItemStyle)
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
              {isExpanded && (
                <span
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  Profile
                </span>
              )}
            </Link>
          )}
          {isLoggedIn && userRole === "student" && (
            <Link
              to="/message-board"
              style={navItemStyle}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, navItemHoverStyle)
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, navItemStyle)
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 6v7a2 2 0 0 1-2 2H8l-4 3V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              {isExpanded && (
                <span
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  Message Board
                </span>
              )}
            </Link>
          )}
          {isLoggedIn && (
            <button
              onClick={handleLogout}
              style={{
                ...navItemStyle,
                marginTop: "20px",
                color: colors.cafeNoir,
              }}
              onMouseOver={(e) =>
                Object.assign(e.currentTarget.style, {
                  backgroundColor: colors.isabelline,
                  color: "#C0392B",
                })
              }
              onMouseOut={(e) =>
                Object.assign(e.currentTarget.style, {
                  ...navItemStyle,
                  marginTop: "20px",
                  color: colors.cafeNoir,
                })
              }
            >
              <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
                <path d="M10.09 15.59L11.5 17l5-5-5-5-1.41 1.41L12.67 11H3v2h9.67l-2.58 2.59zM19 3H5c-1.11 0-2 .9-2 2v4h2V5h14v14H5v-4H3v4c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
              </svg>
              {isExpanded && (
                <span
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  Logout
                </span>
              )}
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}

export default SideBar;
